import { useQuery } from '@tanstack/react-query';
import ChessRestClient from '@/Api/ChessRestClient';
import { getLocalStorage } from '@/Utils/CommonUtils';
import type { RenewalResponse } from '@/Hooks/RenewalApi/useGetRenewalForUser';

/**
 * Response shape matches get_renewal_for_user but without current_subscription.
 * Compatible with RenewalResponse for use in RenewalPaymentScreen.
 */
export type PricingForNewUserResponse = Omit<
  RenewalResponse,
  'current_subscription'
> & {
  current_subscription?: never;
  country?: string;
  currency?: string;
};

export default function useGetPricingForNewUser(
  params?: {level?: string; user_level_source?: string},
  enabled: boolean = true,
) {
  return useQuery<PricingForNewUserResponse>(
    ['pricing-for-new-user', params?.level, params?.user_level_source],
    async () => {
      const token = await getLocalStorage('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }
      const response = await ChessRestClient.get<PricingForNewUserResponse>(
        'subscription/v1/get-pricing-for-new-user/',
        {
          params: {
            level: params?.level,
            user_level_source: params?.user_level_source,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        },
      );
      return response.data;
    },
    {
      enabled: Boolean(enabled),
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    },
  );
}
